import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Button,
    Icon,
} from "@chakra-ui/react";
import { FaMoneyBillWave, FaChartLine, FaShieldAlt, FaArrowRight, FaQuestionCircle } from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import { AFFILIATE_PORTAL_URL, AFFILIATE_LOGIN_URL } from "../../../services/constants";
import { fetchFaqsBySection } from "../../../services/strapi/queries";
import type { FaqItem } from "../../../services/strapi/types";
import {
    DoodleUnderline,
    DoodleSparkle,
    WashiTape,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";

const features = [
    {
        icon: FaMoneyBillWave,
        title: "Earn on every subscription",
        tapeColor: "#FEF08A",
        badge: "Recurring 💰",
        body: "You earn a solid commission for each subscription payment your referred schools make — paid out straight to your Nigerian bank account.",
    },
    {
        icon: FaChartLine,
        title: "Real-time partner portal",
        tapeColor: "#FED7AA",
        badge: "Live Stats 📊",
        body: "Track every referred school, see commissions accrue as terms begin, and request direct bank payouts whenever you are ready.",
    },
    {
        icon: FaShieldAlt,
        title: "Built on Numero rails",
        tapeColor: "#BBF7D0",
        badge: "100% Reliable 🛡️",
        body: "Payouts are automated via Numero, the trusted financial infrastructure running the rest of Result. Safe, prompt, and fully compliant.",
    },
];

const steps = [
    { n: "01", title: "Apply Online", body: "Create your partner profile with your name, phone, and email in under 2 minutes." },
    { n: "02", title: "Confirm Email", body: "Click the verification link we email you to verify your identity." },
    { n: "03", title: "Add Bank Details", body: "Provide your Nigerian bank account and BVN for direct commission payouts." },
    { n: "04", title: "Get Approved", body: "Our team reviews and activates your affiliate dashboard within 1–2 business days." },
    { n: "05", title: "Start Earning", body: "Share your unique referral code with schools. Earn money each time they renew!" },
];

const fallbackFaqs: { q: string; a: string }[] = [
    {
        q: "When do I get paid?",
        a: "You can request a payout any time you have accrued commissions. Payouts are sent directly to your registered Nigerian bank account.",
    },
    {
        q: "How is commission calculated?",
        a: "Each time a school you referred makes a subscription payment to Result, commission is credited to your account. You earn on every payment your schools make.",
    },
    {
        q: "What counts as a referral?",
        a: "A school that signs up using your unique referral code or link and activates their account is permanently attributed to you.",
    },
    {
        q: "Why do you need my BVN?",
        a: "BVN verification is mandatory under Nigerian financial compliance rules to transfer funds to your bank account. It is encrypted and never shared.",
    },
];

import { usePageTitle } from "../../../hooks/usePageTitle";

const AffiliatesPage: React.FC = () => {
    usePageTitle(
        "Affiliate Partner Programme",
        "Earn recurring commissions by introducing primary and secondary schools to Result. Real-time partner dashboard and direct Nigerian bank payouts."
    );
    const [faqs, setFaqs] = useState<{ q: string; a: string }[]>(fallbackFaqs);

    useEffect(() => {
        let cancelled = false;
        fetchFaqsBySection("affiliates").then((items: FaqItem[]) => {
            if (cancelled || items.length === 0) return;
            setFaqs(items.map((it) => ({ q: it.question, a: it.answer })));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="5xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    Partner Programme
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl", "6xl"]} fontWeight="extrabold" lineHeight="1.1">
                            Earn for every school you bring to{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                Result
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg", "xl"]} opacity={0.8} maxW="2xl" lineHeight="1.8">
                            School consultants, EdTech resellers, and educators with networks — recommend Result to schools and earn recurring commissions.
                        </Text>
                        <HStack gap={4} flexWrap="wrap" justify="center" pt={3}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                fontWeight="bold"
                                px={8}
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => (window.location.href = AFFILIATE_PORTAL_URL)}
                            >
                                Apply as an Affiliate <Icon as={FaArrowRight} ml={2} />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => (window.location.href = AFFILIATE_LOGIN_URL)}
                            >
                                Partner Sign In
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            {/* How it pays */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="7xl">
                    <VStack gap={[10, 14]}>
                        <VStack gap={3} textAlign="center">
                            <Text fontSize="xs" fontWeight="extrabold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                How It Pays
                            </Text>
                            <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="gray.900">
                                Real commission on real school revenue
                            </Heading>
                        </VStack>

                        <SimpleGrid columns={[1, 3]} gap={8} w="full">
                            {features.map((f, i) => (
                                <Box
                                    key={f.title}
                                    p={7}
                                    borderRadius="2xl"
                                    border="2.5px solid #0A3D2E"
                                    bg="white"
                                    boxShadow="4px 5px 0px #0A3D2E"
                                    position="relative"
                                    style={{ transform: i % 2 === 0 ? "rotate(-0.5deg)" : "rotate(0.5deg)" }}
                                    transition="all 0.2s ease"
                                    _hover={{ transform: "translate(-2px, -2px)", boxShadow: "6px 7px 0px #0A3D2E" }}
                                >
                                    <WashiTape angle={-3} top="-12px" left="24px" tapeColor={f.tapeColor} label={f.badge} />
                                    <VStack align="start" gap={4} pt={2}>
                                        <Box p={3} bg="#DCFCE7" border="1.5px solid #0A3D2E" borderRadius="xl" color="brand.900">
                                            <Icon as={f.icon} fontSize="xl" />
                                        </Box>
                                        <Heading as="h3" fontSize="lg" fontWeight="extrabold" color="gray.900">
                                            {f.title}
                                        </Heading>
                                        <Text fontSize="sm" color="gray.600" lineHeight="1.8">
                                            {f.body}
                                        </Text>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>
                    </VStack>
                </Container>
            </Box>

            {/* Steps styled as a sequence of notebook cards */}
            <Box py={[16, 20]} bg="#F4F1EA" borderTop="2px solid #0A3D2E" borderBottom="2px solid #0A3D2E">
                <Container maxW="7xl">
                    <VStack gap={[10, 14]}>
                        <VStack gap={3} textAlign="center">
                            <Text fontSize="xs" fontWeight="extrabold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                Getting Started
                            </Text>
                            <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold" color="gray.900">
                                From application to your first payout
                            </Heading>
                        </VStack>

                        <SimpleGrid columns={[1, 2, 5]} gap={6} w="full">
                            {steps.map((s, idx) => (
                                <Box
                                    key={s.n}
                                    p={5}
                                    bg="white"
                                    borderRadius="xl"
                                    border="2px solid #0A3D2E"
                                    boxShadow="3px 3px 0px #0A3D2E"
                                    position="relative"
                                >
                                    {idx === 0 && (
                                        <DoodlePaperclip color="#E11D48" size={28} position="absolute" top="-10px" right="16px" />
                                    )}
                                    <VStack align="start" gap={2}>
                                        <Text
                                            fontSize="2xl"
                                            fontWeight="extrabold"
                                            color="brand.800"
                                            className="font-handwriting"
                                        >
                                            Step {s.n}
                                        </Text>
                                        <Box w="30px" h="2px" bg="gold.500" />
                                        <Text fontWeight="extrabold" fontSize="sm" color="gray.900">{s.title}</Text>
                                        <Text fontSize="xs" color="gray.600" lineHeight="1.6">{s.body}</Text>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>
                    </VStack>
                </Container>
            </Box>

            {/* FAQ */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="3xl">
                    <VStack gap={8}>
                        <VStack gap={2} textAlign="center">
                            <HStack gap={1.5} justify="center">
                                <Icon as={FaQuestionCircle} color="brand.800" />
                                <Text fontSize="xs" fontWeight="extrabold" letterSpacing="widest" textTransform="uppercase" color="brand.800">
                                    Partner FAQ
                                </Text>
                            </HStack>
                            <Heading as="h2" fontSize={["2xl", "3xl"]} fontWeight="extrabold" color="gray.900">
                                Common questions
                            </Heading>
                        </VStack>

                        <VStack gap={4} w="full" align="stretch">
                            {faqs.map((f) => (
                                <Box
                                    key={f.q}
                                    p={5}
                                    borderRadius="xl"
                                    border="2px solid #0A3D2E"
                                    bg="white"
                                    boxShadow="3px 3px 0px #0A3D2E"
                                >
                                    <Text fontWeight="extrabold" fontSize="md" color="gray.900" mb={2}>
                                        {f.q}
                                    </Text>
                                    <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                                        {f.a}
                                    </Text>
                                </Box>
                            ))}
                        </VStack>
                    </VStack>
                </Container>
            </Box>

            {/* Bottom CTA */}
            <Box py={[16, 20]} bg="brand.900" color="white" borderTop="3px solid" borderColor="gold.500">
                <Container maxW="3xl">
                    <VStack gap={6} textAlign="center">
                        <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold">
                            Ready to start earning?
                        </Heading>
                        <Text fontSize="md" opacity={0.8} maxW="lg">
                            Apply now — most applications are reviewed and approved within 1–2 business days.
                        </Text>
                        <HStack gap={4} flexWrap="wrap" justify="center" pt={2}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                fontWeight="bold"
                                px={8}
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => (window.location.href = AFFILIATE_PORTAL_URL)}
                            >
                                Apply Now
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => (window.location.href = AFFILIATE_LOGIN_URL)}
                            >
                                Sign In
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default AffiliatesPage;
